import "@app/App.css";
import { ValueEditor } from "@app/fields/ValueEditor";
import { Button } from "@comps/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@comps/ui/command";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@comps/ui/form";
import { Input } from "@comps/ui/input";
import { Label } from "@comps/ui/label";
import { ScrollArea } from "@comps/ui/scroll";
import { Textarea } from "@comps/ui/textarea";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import Field, { AggregationType, FIELD_TYPES_WITH_FORMULA, FieldType, FieldTypeChanges, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_OBJECT_KEY, TYPE_ITEMS_RELATION_CARDINALITY, TYPE_ITEMS_RELATION_FILTER, TYPE_ITEMS_RELATION_KEY, TYPE_ITEMS_RELATION_NAME, fieldTypeToIcon, hasDefaultValueAfterTypeItems, hasDefaultValueBeforeTypeItems, hasFormatting, validAggregationByType } from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { Filter, INBOUND, OUTBOUND } from "@dto/records";
import { ObjectRelation, getDestCard, getSrcCard } from "@dto/relation";
import { zodResolver } from "@hookform/resolvers/zod";
import { BASE_URL } from "@utils/constants";
import { checkFormula, fetchFieldTypeChanges, getRelationDirection } from "@utils/model";
import { TW_AddFieldDefaultValueLabel, TW_AddFieldDefaultValueTitle, TW_AddFieldDescriptionLabel, TW_AddFieldDescriptionTitle, TW_AddFieldTypeArrow, TW_AddFieldTypeDiv, TW_AddFieldTypeIcon, TW_AddFieldTypeItem, TW_AddFieldTypeScrollArea, TW_AddFieldTypeText, TW_AppDialogButton, TW_ButtonList, TW_CheckboxCell_not_selected, TW_CheckboxCell_selected, TW_CommonCommandInput, TW_ErrorMessage, TW_FieldDescription_Input, TW_FieldFormatSectionLine, TW_FieldFormatSectionLine_Label, TW_FieldMenuItem, TW_FieldSection, TW_FieldSection_Label, TW_FieldUpdateName_Input, TW_FieldUpdateType_Input, TW_UpdateFieldDialogContent, TW_UpdateFieldDialogContentWide, TW_UpdateFieldForm, TW_UpdateFieldTypeItemSelected, getWsButtonStyle, useHover } from "@utils/twClasses";
import { idToKey } from "@utils/types";
import { post, put } from "@utils/utils";
import { ChevronDown, ChevronUp, Edit2, Minus, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { translate } from "src/i18n/i18n";
import { useDataStore, useModelStore } from "src/store";
import { z } from "zod";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { FIELD_TYPES_WITH_AGGREGATION, FieldAggregationForm } from "./FieldAggregationForm";
import { FieldFormatSection } from "./FieldFormatSection";
import { FieldFormulaSection } from "./FieldFormulaSection";
import { FIELD_TYPES_WITH_TYPE_ITEMS, FieldTypeItemsForm } from "./FieldTypeItemsForm";
import { newRelationKey } from "./LinkTypeItemsForm";
import { isTypeSelectable, keepsFormat, keepsFormula, keepsTypeItems } from "./fieldTypeChange";

interface FieldMenuUpdateItemProps {
  field: Field;
  onFieldUpdate: (field: Field) => void;
  closeMenu: () => void;
}

function FieldMenuUpdateItem({ field, onFieldUpdate, closeMenu }: FieldMenuUpdateItemProps) {

  const { t } = useTranslation();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [hasRelationFilter, setHasRelationFilter] = useState(false);
  const wsHoverColor = useModelStore(state => state.wsHoverColor);
  const hover = useHover(getWsButtonStyle('transparent'), getWsButtonStyle(wsHoverColor));


  const fieldUpdated = (field: Field) => {
    onFieldUpdate(field);
    setIsDialogOpen(false);
    closeMenu();
  }

  function cancel(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
    setIsDialogOpen(false);
    setHasRelationFilter(false);
    closeMenu();
  }

  function changeOpenState(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      setHasRelationFilter(false);
      closeMenu();
    }
  }

  return (
    <Dialog open={isDialogOpen} onOpenChange={changeOpenState}>
      <DialogTrigger className={TW_FieldMenuItem + ` ${field === null ? "item-disabled" : ""}`} {...hover}><Edit2 className="FieldMenuItemIcon" />{t("field.button.update.text")}</DialogTrigger>
      <DialogContent onOpenAutoFocus={(event) => event.preventDefault()} className={hasRelationFilter ? TW_UpdateFieldDialogContentWide : TW_UpdateFieldDialogContent}>
        <DialogHeader>
          <DialogTitle>{t("field.dialog.update.title")}</DialogTitle>
          <DialogDescription hidden>{t("field.dialog.update.description")}</DialogDescription>
        </DialogHeader>
        {field && <FieldUpdateForm initialField={field} onFieldUpdate={fieldUpdated} onCancel={cancel} onHasRelationFilterChange={setHasRelationFilter} />}
      </DialogContent>
    </Dialog>
  )
}

interface FieldUpdateFormProps {
  initialField: Field;
  onFieldUpdate: (field: Field) => void;
  onCancel: (event: React.MouseEvent<HTMLButtonElement>) => void;
  onHasRelationFilterChange: (hasFilter: boolean) => void;
}

function FieldUpdateForm({ initialField, onFieldUpdate, onCancel, onHasRelationFilterChange }: FieldUpdateFormProps) {

  const { t } = useTranslation();
  const workspace = useModelStore(state => state.selectedWorkspace);
  const updateSelectedObjectInfos = useModelStore(state => state.updateSelectedObjectInfos)
  const [error, setError] = useState<string | null>(null);
  const selectedObjectInfos = useModelStore(state => state.selectedObjectInfos);
  const updateSingleObjectInfo = useModelStore(state => state.updateSingleObjectInfo);
  const objectInfos = useModelStore(state => state.objectInfos);
  const relationByKeys = useModelStore(state => state.relationByKeys);
  const sharedObjectInfos = useModelStore(state => state.sharedObjectInfos);
  const fetchData = useDataStore(state => state.fetchData);
  const wsColor = useModelStore(state => state.wsColor);
  const wsLightColor = useModelStore(state => state.wsLightColor);
  const wsDarkColor = useModelStore(state => state.wsDarkColor);
  const cancelHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const confirmHover = useHover(getWsButtonStyle(wsColor), getWsButtonStyle(wsDarkColor));
  const [hasDescription, setHasDescription] = useState(initialField.description && initialField.description.trim() !== "");
  const initialHasDefaultValue = initialField.defaultValue !== undefined && initialField.defaultValue !== null && initialField.defaultValue !== "";
  const [hasDefaultValue, setHasDefaultValue] = useState(initialHasDefaultValue);
  // Type change: the selected type drives the form, initialField keeps the persisted definition
  const [selectedType, setSelectedType] = useState<FieldType>(initialField.type);
  const [typeSelectorOpen, setTypeSelectorOpen] = useState(false);
  // Type changes allowed by the backend (null while loading or on error: the type selector stays disabled)
  const [typeChanges, setTypeChanges] = useState<FieldTypeChanges | null>(null);
  // Type items as currently edited (the sub-forms write them into the form, this mirror keeps the derived field reactive)
  const [currentTypeItems, setCurrentTypeItems] = useState<Map<string, any> | undefined>(initialField.typeItems);

  const scrollStyle = {
    scrollbarColor: wsColor + " " + wsLightColor
  }


  const formSchema = z.object({
    fieldName: z.string().min(1, {
      message: t("field.dialog.error.name.missing"),
    }),
    fieldType: z.nativeEnum(FieldType).optional(),
    fieldFormat: z.map(z.string(), z.any()).optional(),
    fieldTypeItems: z.map(z.string(), z.any()).optional(),
    fieldAggregation: z.nativeEnum(AggregationType).optional(),
    fieldFormula: z.string().optional(),
    fieldDescription: z.string().optional(),
    fieldReadOnly: z.boolean().optional(),
    fieldDefaultValue: z.any().optional(),
  })

  const form = useForm<z.infer<typeof formSchema>>({
    values: {
      fieldName: initialField.name,
      fieldType: initialField.type,
      fieldFormat: initialField.format,
      fieldTypeItems: initialField.typeItems,
      fieldAggregation: initialField.aggregation,
      fieldFormula: initialField.formula,
      fieldDescription: initialField.description,
      fieldReadOnly: initialField.readOnly,
      fieldDefaultValue: initialField.defaultValue,
    },
    resolver: zodResolver(formSchema),
  })

  const wsKey = workspace?._key;
  const objKey = selectedObjectInfos?.object._key;
  useEffect(() => {
    // A link field never changes type: no need to ask the backend
    if (initialField.type === FieldType.Link) {
      return;
    }
    let cancelled = false;
    fetchFieldTypeChanges(wsKey, objKey, initialField._key, changes => {
      if (!cancelled) {
        setTypeChanges(changes);
      }
    });
    return () => { cancelled = true; };
  }, [initialField._key, initialField.type, wsKey, objKey]);

  // Field definition as currently edited: identical to the persisted one while the type is unchanged
  const currentField: Field = useMemo(() => {
    if (selectedType === initialField.type) {
      return initialField;
    }
    return {
      ...initialField,
      type: selectedType,
      typeItems: currentTypeItems,
      format: keepsFormat(initialField.type, selectedType) ? initialField.format : undefined,
      aggregation: AggregationType.NO_AGGR,
      defaultValue: undefined,
    };
  }, [initialField, selectedType, currentTypeItems]);

  function onTypeChange(type: FieldType) {
    setTypeSelectorOpen(false);
    if (type === selectedType) {
      return;
    }
    setSelectedType(type);
    form.setValue("fieldType", type);
    // Every reset is relative to the persisted definition, so going back to the initial type restores it
    const typeItems = keepsTypeItems(initialField.type, type) ? initialField.typeItems : new Map<string, any>();
    setCurrentTypeItems(typeItems);
    form.setValue("fieldTypeItems", typeItems);
    form.setValue("fieldFormat", keepsFormat(initialField.type, type) ? initialField.format : new Map<string, any>());
    const backToInitial = type === initialField.type;
    form.setValue("fieldAggregation", backToInitial ? initialField.aggregation : AggregationType.NO_AGGR);
    form.setValue("fieldDefaultValue", backToInitial ? initialField.defaultValue : undefined);
    setHasDefaultValue(backToInitial && initialHasDefaultValue);
    form.setValue("fieldFormula", backToInitial ? initialField.formula : keepsFormula(type) ? form.getValues("fieldFormula") : "");
    onHasRelationFilterChange(false);
  }

  function submitRelationField(values: z.infer<typeof formSchema>) {

    const tItems = values.fieldTypeItems;

    if (!tItems) {
      setError(t("field.dialog.error.link.destination.missing"));
      return;
    }

    const objectKey = tItems.get(TYPE_ITEMS_OBJECT_KEY);
    const fieldKey = tItems.get(TYPE_ITEMS_FIELD_KEY);
    const relationName = tItems.get(TYPE_ITEMS_RELATION_NAME);
    const relationCardinality = tItems.get(TYPE_ITEMS_RELATION_CARDINALITY);
    let relationFilter = tItems.get(TYPE_ITEMS_RELATION_FILTER);
    let relationKey = tItems.get(TYPE_ITEMS_RELATION_KEY);
    if (!objectKey || !fieldKey) {
      setError(t("field.dialog.error.link.destination.missing"));
      return;
    }
    if (!relationKey) {
      setError(t("field.dialog.error.link.relation.key.missing"));
      return;
    }

    if (relationFilter && relationFilter.groupFilter && relationFilter.groupFilter.filters.length === 0) {
      relationFilter = null;
    }

    if (relationKey === newRelationKey) {
      if (!relationName) {
        setError(t("field.dialog.error.link.relation.name.missing"));
        return;
      }

      const relationBody = {
        name: relationName,
        _from: selectedObjectInfos!.object._key,
        _to: objectKey,
        srcCard: getSrcCard(relationCardinality),
        destCard: getDestCard(relationCardinality),
        filter: relationFilter ?? null,
      }
      post<ObjectRelation>(`${BASE_URL}/model/workspaces/${workspace!._key}/relations`, relationBody).then(newRelation => {
        if (isCompozzError(newRelation)) {
          const cError = newRelation as CompozzError;
          if (cError.i18n) {
            setError(translate(t, cError.i18n, cError.params));
          } else {
            setError(cError.message);
          }
        } else {
          setError(null);
          relationKey = (newRelation as ObjectRelation)?._key;

          if (!relationKey) {
            setError(t("field.dialog.error.link.relation.key.missing"));
            return;
          }

          const newFieldTypeItems = new Map<string, any>(values.fieldTypeItems)
          newFieldTypeItems.set(TYPE_ITEMS_RELATION_KEY, relationKey!)
          newFieldTypeItems.delete(TYPE_ITEMS_RELATION_NAME)
          newFieldTypeItems.delete(TYPE_ITEMS_RELATION_CARDINALITY)
          newFieldTypeItems.delete(TYPE_ITEMS_RELATION_FILTER)
          const newFieldValues: z.infer<typeof formSchema> = { ...values, fieldTypeItems: newFieldTypeItems };
          const newRelations = [...(selectedObjectInfos!.relations || []), newRelation!];
          const newObjectInfos = { ...selectedObjectInfos!, relations: newRelations };
          submitField(newObjectInfos, newFieldValues);
        }
      })
    } else {

      const relation = relationByKeys?.get(relationKey);
      const direction = getRelationDirection(relation, selectedObjectInfos?.object._key);

      let srcCard = getSrcCard(relationCardinality);
      let destCard = getDestCard(relationCardinality);
      if (direction === INBOUND) {
        const oldSrcCard = srcCard
        srcCard = destCard
        destCard = oldSrcCard
      }

      let oldRelationFilter: Filter | null | undefined = null;
      if (direction === OUTBOUND) {
        oldRelationFilter = relation?.destFilter ?? null;
      } else if (direction === INBOUND) {
        oldRelationFilter = relation?.srcFilter ?? null;
      }
      // If the relation cardinality or filter has changed, update the relation
      const filterChanged = JSON.stringify(relationFilter ?? null) !== JSON.stringify(oldRelationFilter);
      if (relation && (srcCard !== relation?.srcCard || destCard !== relation?.destCard || filterChanged)) {
        const relationBody = {
          _key: relationKey,
          name: relationName,
          _from: idToKey(relation!._from),
          _to: idToKey(relation!._to),
          srcCard: srcCard,
          destCard: destCard,
          srcFilter: direction === INBOUND ? relationFilter ?? null : relation?.srcFilter ?? null,
          destFilter: direction === OUTBOUND ? relationFilter ?? null : relation?.destFilter ?? null,
        }
        put<ObjectRelation>(`${BASE_URL}/model/workspaces/${workspace!._key}/relations`, relationBody).then(newRelation => {
          if (isCompozzError(newRelation)) {
            const cError = newRelation as CompozzError;
            if (cError.i18n) {
              setError(translate(t, cError.i18n, cError.params));
            } else {
              setError(cError.message);
            }
          } else {
            setError(null);
            const newFieldTypeItems = new Map<string, any>(values.fieldTypeItems)
            newFieldTypeItems.delete(TYPE_ITEMS_RELATION_NAME)
            newFieldTypeItems.delete(TYPE_ITEMS_RELATION_CARDINALITY)
            newFieldTypeItems.delete(TYPE_ITEMS_RELATION_FILTER)
            const newFieldValues: z.infer<typeof formSchema> = { ...values, fieldTypeItems: newFieldTypeItems };

            if (newRelation) {
              const newRelations = (selectedObjectInfos!.relations || []).map(r => r._key === relationKey ? newRelation! : r);
              const newSelectedObjectInfos: ObjectInfos = { ...selectedObjectInfos!, relations: newRelations };
              submitField(newSelectedObjectInfos, newFieldValues);

              // If direction is inbound, relation has to be updated to the from object
              if (direction === INBOUND) {
                const srcObjectInfo = objectInfos?.find(o => o.object._key === idToKey(relation!._from)) ?? null;
                const newSrcObjRelations = (srcObjectInfo!.relations || []).map(r => r._key === relationKey ? newRelation! : r);
                const newSrcObjectInfos: ObjectInfos = { ...srcObjectInfo!, relations: newSrcObjRelations };
                updateSingleObjectInfo(objectInfos, newSrcObjectInfos);
              }
            } else {
              submitField(selectedObjectInfos!, newFieldValues);
            }
          }
        })

      } else {
        const newFieldTypeItems = new Map<string, any>(values.fieldTypeItems)
        newFieldTypeItems.delete(TYPE_ITEMS_RELATION_NAME)
        newFieldTypeItems.delete(TYPE_ITEMS_RELATION_CARDINALITY)
        newFieldTypeItems.delete(TYPE_ITEMS_RELATION_FILTER)
        const newFieldValues: z.infer<typeof formSchema> = { ...values, fieldTypeItems: newFieldTypeItems };
        submitField(selectedObjectInfos!, newFieldValues);
      }
    }
  }

  function submitField(objectInfos: ObjectInfos, values: z.infer<typeof formSchema>) {

    const body: Field = {
      ...initialField,
      name: values.fieldName,
      type: values.fieldType ?? initialField.type,
      format: values.fieldFormat,
      typeItems: values.fieldTypeItems,
      aggregation: values.fieldAggregation,
      formula: values.fieldFormula || "",
      description: values.fieldDescription || "",
      readOnly: values.fieldReadOnly || false,
      defaultValue: values.fieldDefaultValue ?? undefined,
    }

    put<Field>(`${BASE_URL}/model/workspaces/${workspace!._key}/objects/${objectInfos!.object._key}/fields`, body).then(newField => {
      if (isCompozzError(newField)) {
        const cError = newField as CompozzError;
        if (cError.i18n) {
          setError(translate(t, cError.i18n, cError.params));
        } else {
          setError(cError.message);
        }
      } else {
        setError(null);

        if (objectInfos) {
          const newFields = objectInfos.fields.map(f => f._key === body._key ? body : f);
          const newSelectedObjectInfos = { ...objectInfos, fields: newFields };
          updateSelectedObjectInfos(newSelectedObjectInfos, null, null, sharedObjectInfos);
        }
        fetchData();

        // The endpoint answers 204: the field as sent is the updated definition
        onFieldUpdate(body);
      }
    })
  }

  function onSubmit(values: z.infer<typeof formSchema>) {
    setError(null);
    if (!workspace || !selectedObjectInfos) {
      console.error("No workspace or object selected");
      return;
    }

    if (values.fieldFormula && values.fieldFormula !== "") {
      checkFormula(workspace!._key, selectedObjectInfos!.object._key, initialField._key, values.fieldFormula, () => {
        if (selectedType === FieldType.Link) {
          submitRelationField(values);
        } else {
          submitField(selectedObjectInfos!, values);
        }
      }, (errorI18n, _, errorParams) => {
        setError(translate(t, errorI18n, errorParams));
      });
    } else {
      if (selectedType === FieldType.Link) {
        submitRelationField(values);
      } else {
        submitField(selectedObjectInfos!, values);
      }
    }
  }

  function onEnterKeyPress(event: React.KeyboardEvent) {
    if (event.key === "Enter") {
      event.preventDefault();
      event.stopPropagation();
      form.handleSubmit(onSubmit)();
    }
  }

  function onFormatChange(formatKey: string, isCheckbox: boolean) {
    return (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      let newValue: any = event.target.value;
      if (isCheckbox && event.target instanceof HTMLInputElement) {
        newValue = event.target.checked;
      }
      const newFormat = new Map(form.getValues("fieldFormat"));
      newFormat.set(formatKey, newValue);
      form.setValue("fieldFormat", newFormat);
    }
  }

  function onFormulaChange(formula: string) {
    form.setValue("fieldFormula", formula);
  }

  function onAggregationChange(aggregationType: AggregationType) {
    form.setValue("fieldAggregation", aggregationType);
  }

  const validAggregationTypes = validAggregationByType(currentField.type, currentField.typeItems);

  function showAggregation() {
    if (!currentField.type) {
      return false;
    }
    if (currentField.type !== FieldType.Link) {
      return false;
    }
    return validAggregationTypes.length > 1 && FIELD_TYPES_WITH_AGGREGATION.includes(currentField.type);
  }

  const hasAggregation = showAggregation();

  function onTypeItemsChange(typeItems: Map<string, any>) {
    setCurrentTypeItems(typeItems);
    // Check if we have a link field with a relation filter
    if (selectedType === FieldType.Link) {
      const relationFilter = typeItems.get(TYPE_ITEMS_RELATION_FILTER);
      onHasRelationFilterChange(relationFilter != null);
    } else {
      onHasRelationFilterChange(false);
    }
  }

  function cancel(e: React.MouseEvent<HTMLButtonElement>) {
    e.preventDefault();
    e.stopPropagation();
    onCancel(e);
    onHasRelationFilterChange(false);
  }

  const handleAddDescription = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasDescription) {
      form.setValue("fieldDescription", undefined);
    } else {
      form.setValue("fieldDescription", initialField.description);
    }
    setHasDescription(!hasDescription);
  };

  const handleAddDefaultValue = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    if (hasDefaultValue) {
      form.setValue("fieldDefaultValue", undefined);
    }
    setHasDefaultValue(!hasDefaultValue);
  };

  const handleRemoveDefaultValue = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    e.preventDefault();
    form.setValue("fieldDefaultValue", undefined);
    setHasDefaultValue(false);
  };

  function onDefaultValueChange(value: any) {
    const defaultValue = value;
    if (defaultValue === undefined || defaultValue === null || defaultValue === "") {
      form.setValue("fieldDefaultValue", undefined);
    } else {
      form.setValue("fieldDefaultValue", value);
    }
  }

  // The type can be changed once the backend told which changes are possible; a link field never changes type
  const typeChangeEnabled = initialField.type !== FieldType.Link && typeChanges !== null;
  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className={TW_UpdateFieldForm}>

        <div className={TW_FieldSection}>
          <p className={TW_FieldSection_Label}>{t(`field.parameters.label`)}</p>
          <div className={TW_FieldFormatSectionLine}>
            <p className={TW_FieldFormatSectionLine_Label}>{t("field.dialog.name.label")}</p>
            <FormField
              control={form.control}
              name="fieldName"
              render={({ field }) => (
                <FormItem className="w-1/2">
                  <FormControl>
                    <Input className={TW_FieldUpdateName_Input} placeholder={t("field.dialog.name.placeholder")} {...field} onKeyDown={onEnterKeyPress} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <div className={TW_FieldFormatSectionLine}>
            <p className={TW_FieldFormatSectionLine_Label + " " + (typeChangeEnabled ? "" : "opacity-50")}>{t("field.dialog.type.label")}</p>
            <FormField
              control={form.control}
              name="fieldType"
              render={({ field }) => (
                <FormItem className="w-1/2">
                  <FormControl>
                    <>
                      {field.value && field.value !== undefined && (
                        <Command className={TW_FieldUpdateType_Input}>
                          <CommandItem className="w-full"
                            disabled={!typeChangeEnabled}
                            onSelect={() => setTypeSelectorOpen(!typeSelectorOpen)}>
                            {fieldTypeToIcon(field.value, TW_AddFieldTypeIcon)}
                            <span className={TW_AddFieldTypeText}>{t(`field.type.${field.value}`)}</span>
                            {typeChangeEnabled && (typeSelectorOpen ? <ChevronUp className={TW_AddFieldTypeArrow} /> : <ChevronDown className={TW_AddFieldTypeArrow} />)}
                          </CommandItem>
                        </Command>
                      )}
                    </>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          {typeSelectorOpen && (
            // The type list takes the whole dialog width, below the type line
            <div className={TW_AddFieldTypeDiv}>
              <Command>
                <CommandInput placeholder={t("data.type.search.placeholder")} className={TW_CommonCommandInput} />
                <ScrollArea className={TW_AddFieldTypeScrollArea} style={scrollStyle}>
                  <CommandList className="h-full max-h-full">
                    <CommandEmpty>{t("data.type.search.noResults")}</CommandEmpty>
                    <CommandGroup>
                      {Object.values(FieldType).filter(type => type !== FieldType.Link).map((type) => (
                        <CommandItem
                          key={type}
                          className={selectedType === type ? TW_UpdateFieldTypeItemSelected : TW_AddFieldTypeItem}
                          disabled={!isTypeSelectable(type, selectedType, typeChanges)}
                          onSelect={() => onTypeChange(type)}>
                          {fieldTypeToIcon(type, "AddFieldTypeIcon")}
                          <span className="AddFieldTypeText">{t(`field.type.${type}`)}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </ScrollArea>
              </Command>
            </div>
          )}
          {typeChanges?.hasValues && (
            <p className="text-xs text-gray-500 italic">{t("field.dialog.type.hint.hasValues")}</p>
          )}
          <div className="flex items-center gap-2 mt-2 w-full">
            <Label htmlFor="fieldReadOnly" className="cursor-pointer text-sm grow">{t("field.dialog.readOnly.label")}</Label>
            <FormField
              control={form.control}
              name="fieldReadOnly"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input className={(field.value ? TW_CheckboxCell_selected : TW_CheckboxCell_not_selected)} type="checkbox" checked={field.value} onChange={field.onChange} />

                  </FormControl>
                </FormItem>
              )}
            />
          </div>
          {hasDefaultValueBeforeTypeItems(selectedType) && !hasDefaultValue && (
            <div className={TW_AddFieldDefaultValueTitle + " mt-0 mb-2 h-9"} onClick={handleAddDefaultValue}>
              <Plus className="text-gray-800 h-4 w-4" />
              <Label className={TW_AddFieldDefaultValueLabel + " cursor-pointer"}>{t("field.dialog.defaultValue.add")}</Label>
            </div>
          )}
          {hasDefaultValueBeforeTypeItems(selectedType) && hasDefaultValue && (
            <FormField
              control={form.control}
              name="fieldDefaultValue"
              render={({ field }) => (
                <FormItem className="flex justify-start items-center gap-2 mt-0 mb-2 w-full h-9">
                  <FormControl >
                    <>
                      <Label htmlFor="fieldDefaultValue" className="text-sm grow ">{t("field.dialog.defaultValue.label")}</Label>
                      <ValueEditor format={form.getValues("fieldFormat") as Map<string, any>} typeItems={form.getValues("fieldTypeItems") as Map<string, any>} type={selectedType} value={field.value} disabled={false} onValueChange={onDefaultValueChange} className="w-44" />
                      <Trash2 className="text-gray-800 h-4 w-4 cursor-pointer" onClick={handleRemoveDefaultValue} />
                    </>
                  </FormControl>
                </FormItem>
              )}
            />)}
        </div>

        {FIELD_TYPES_WITH_TYPE_ITEMS.includes(selectedType) && (
          <FieldTypeItemsForm field={currentField} form={form as any} onTypeItemsChange={onTypeItemsChange} setError={setError} updateAggregation={onAggregationChange} />
        )}
        {hasDefaultValueAfterTypeItems(selectedType) && !hasDefaultValue && (
          <div className={TW_AddFieldDefaultValueTitle + " mt-0 mb-2 h-9"} onClick={handleAddDefaultValue}>
            <Plus className="text-gray-800 h-4 w-4" />
            <Label className={TW_AddFieldDefaultValueLabel + " cursor-pointer"}>{t("field.dialog.defaultValue.add")}</Label>
          </div>
        )}
        {hasDefaultValueAfterTypeItems(selectedType) && hasDefaultValue && (
          <FormField
            control={form.control}
            name="fieldDefaultValue"
            render={({ field }) => (
              <FormItem className="flex justify-start items-center gap-2 mt-0 mb-2 w-full h-9">
                <FormControl>
                  <>
                    <Label htmlFor="fieldDefaultValue" className="text-sm grow w-1/2">{t("field.dialog.defaultValue.label")}</Label>
                    <ValueEditor format={form.getValues("fieldFormat") as Map<string, any>} typeItems={form.getValues("fieldTypeItems") as Map<string, any>} type={selectedType} value={field.value} disabled={false} onValueChange={onDefaultValueChange} />
                    <Trash2 className="text-gray-800 h-4 w-4 cursor-pointer" onClick={handleRemoveDefaultValue} />
                  </>
                </FormControl>
              </FormItem>
            )}
          />)}
        {hasFormatting(currentField) && (
          <FormField
            control={form.control}
            name="fieldFormat"
            render={({ field }) => (
              <div className={TW_FieldSection}>
                <p className={TW_FieldSection_Label}>{t(`field.format.label`)}</p>
                <FieldFormatSection fieldType={currentField.type} fieldTypeItems={currentField.typeItems} format={field.value} aggregation={currentField.aggregation} onFormatChange={onFormatChange} />
              </div>
            )}
          />
        )}
        {FIELD_TYPES_WITH_FORMULA.includes(selectedType) && (
          <FormField
            control={form.control}
            name="fieldFormula"
            render={({ field }) => (
              <FieldFormulaSection formula={field.value} onFormulaChange={onFormulaChange} currentFieldKey={initialField._key} />
            )}
          />
        )}
        {hasAggregation && (
          <FormField
            control={form.control}
            name="fieldAggregation"
            render={({ field }) => (
              <FieldAggregationForm fieldType={currentField.type} fieldTypeItems={currentField.typeItems} fieldAggregation={field.value} onAggregationChange={onAggregationChange} setError={setError} />
            )}
          />
        )}
        {hasDescription && (
          <FormField
            control={form.control}
            name="fieldDescription"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <div className={TW_FieldSection + " mt-2"}>
                    <p className={TW_FieldSection_Label}>{t(`field.dialog.description.label`)}</p>
                    <Textarea className={TW_FieldDescription_Input} style={scrollStyle} placeholder={t("field.dialog.description.placeholder")} {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        {error && <div className={TW_ErrorMessage}>{error}</div>}
        {!error && <div className={TW_ErrorMessage}></div>}
        <div className={TW_ButtonList}>
          <div className={TW_AddFieldDescriptionTitle} onClick={handleAddDescription}>
            {hasDescription ? (
              <>
                <Minus className="text-gray-800 h-4 w-4" />
                <Label className={TW_AddFieldDescriptionLabel + " cursor-pointer"}>{t("field.dialog.description.remove")}</Label>
              </>
            ) : (
              <>
                <Plus className="text-gray-800 h-4 w-4" />
                <Label className={TW_AddFieldDescriptionLabel + " cursor-pointer"}>{t("field.dialog.description.add")}</Label>
              </>
            )}
          </div>
          <Button className={TW_AppDialogButton} onClick={cancel} {...cancelHover}>{t("general.button.cancel.text")}</Button>
          <Button className={TW_AppDialogButton} type="submit" {...confirmHover}>{t("general.button.ok.text")}</Button>
        </div>
      </form>
    </Form>
  )
}

export default FieldMenuUpdateItem;
